import isType from '../../../io-ts/isType';
import type { Form } from '../converters/types/Form';
import { FormC } from '../converters/types/Form';
import type { FieldCommonProps } from '../types/FieldCommonProps';
import { SelectFieldC } from '../converters/functions/toSelectFormat';
import { FileFieldC } from '../converters/functions/toFileUpload';
import { UsersGroupsChoiceC } from '../converters/functions/toUsersGroupsChoice';
import { RadioChoiceC } from '../converters/functions/toRadioChoice';
import { RetentionRuleFieldC } from '../converters/functions/toRetentionRule';
import { InputFieldC } from '../converters/functions/toInputFormat';
import { AudioFieldC } from '../converters/functions/toAudioFormat';
import { MultiFilesFieldC } from '../converters/functions/toMultiFilesFormat';
import { MultiSelectFieldC } from '../converters/functions/toMultiSelectFormat';
import { StaticTextFieldC } from '../converters/functions/toStaticText';
import { StaticImageFieldC } from '../converters/functions/toStaticImage';
import {
    ArrayFieldC,
    ArrayWithPrototypeFieldC,
} from '../converters/functions/toArrayFormat';
import { UploadLinkExpirationFieldC } from '../converters/functions/toUploadLinkExpiration';
import { ExpirationFieldC } from '../converters/functions/toExpiration';
import { CheckboxFieldC } from '../converters/functions/toCheckboxFormat';
import { RadioFieldC } from '../converters/functions/toRadioFormat';
import { DateFieldC } from '../converters/functions/toDateType';
import FIELD_RENDER_TYPES from '../../fieldRenderTypes';

const flatMapForm = (form: Form): FieldCommonProps[] => Object.values(form.fields)
    .flatMap((field) => (isType(field, FormC) ? flatMapForm(field) : field));

const getRequiredFields = (form: Form) => flatMapForm(form)
    .filter((field: FieldCommonProps) => field.isRequired ?? false);

const valueFieldTypes = [
    InputFieldC,
    AudioFieldC,
];

const groupFieldTypes = [
    UsersGroupsChoiceC,
    RadioChoiceC,
    RetentionRuleFieldC,
];

const isStaticField = (field: FieldCommonProps) => [
    StaticTextFieldC,
    StaticImageFieldC,
].some((f) => f.is(field));

/** Optional compound controls — server validates nested rules on submit. */
const isOptionalCompoundField = (field: FieldCommonProps): boolean => [
    UploadLinkExpirationFieldC,
    ExpirationFieldC,
    CheckboxFieldC,
    RadioFieldC,
].some((type) => isType(field, type));

const validateField = (field: FieldCommonProps): boolean => {
    const isValueField = valueFieldTypes.some((type) => isType(field, type));
    if (isValueField) {
        // Hidden inputs (e.g. nameTitle) are structural, not user-filled.
        if (field.renderAs === FIELD_RENDER_TYPES.NONE || field.type === 'hidden') {
            return true;
        }
        if (field.type === 'string') {
            return Boolean((field.value as string).trim());
        }
        return Boolean(field.value);
    }

    if (isType(field, SelectFieldC)) {
        return true;
    }

    if (isType(field, MultiSelectFieldC)) {
        return field.selectedChoices.length > 0;
    }

    if (isType(field, FileFieldC)) {
        return field.multiple
            ? field.files.length > 0
            : field.file !== null;
    }

    if (isType(field, MultiFilesFieldC)) {
        return field.files.length > 0 || field.uploadFiles.length > 0;
    }

    if (isType(field, ArrayFieldC) || isType(field, ArrayWithPrototypeFieldC)) {
        return field.items.length > 0;
    }

    if (isType(field, DateFieldC)) {
        return Boolean(field.value);
    }

    if (isStaticField(field)) {
        return true;
    }

    const isGroupField = groupFieldTypes.some((type) => isType(field, type));
    if (isGroupField) {
        return true;
    }

    if (isOptionalCompoundField(field)) {
        return true;
    }

    throw new Error(`No required validator for field ${field.renderAs}.`);
};

export const validate = (form: Form): boolean => getRequiredFields(form).every(validateField);
